import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Button,
  Container,
  Chip,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Grid,
  TextField,
  Divider
} from "@mui/material";
import UserDialog from "../sections/users/UserFormDialog";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { DataGrid } from "@mui/x-data-grid";
import { AppContext } from "../context/AppProvider";
import { AuthContext } from "../context/AuthProvider";
import { cloneDeep } from "lodash";
import ConfirmDialog from "../components/ConfirmDialog";
import ChangePasswordDialog from "../sections/users/ChangePasswordDialog";
import { fetchWithInterceptor } from "../utils/fetchWithInterceptor";

const PAGE_SIZE = 20;

const defaultOrder = [ { field: "createdAt", sort: "desc" } ];

export default function UsersPage() {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  const appContext = useContext(AppContext);
  const authContext = useContext(AuthContext);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sortModel, setSortModel] = useState(defaultOrder);
  const [dialogRowId, setDialogRowId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);

  const active = useRef(true);

  const [filters, setFilters] = useState({
    fullName: "",
    email: "",
    role: "",
  });

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
  });

  const defaultFilter = {
  fullName: "",
  email: "",
  role: "",
  }

const handleReset = () => setFilters(cloneDeep(defaultFilter));

  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleMenuOpen = (event, user) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleToggleStatus = async () => {
    if (!selectedUser) return;

    try {
      const newStatus = !selectedUser.isActive;

      const res = await fetchWithInterceptor(`/api/user/update/${selectedUser.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": authContext.getToken(),
        },
        body: JSON.stringify({ isActive: newStatus }),
      });

      if (!res.ok) throw new Error("Failed updating user status");

      appContext.showMessage(
        newStatus ? "User activated" : "User deactivated",
        "success"
      );

      handleMenuClose();
      loadUsers(true);

    } catch (err) {
      console.error(err);
      appContext.showMessage("Error updating user status", "error");
    }
  };

  const handleToggleStatusClick = () => {
    setPendingAction(() => handleToggleStatus);
    setConfirmOpen(true);
  };

  const handleChangePassword = () => {
    setPasswordDialogOpen(true);
    handleMenuClose();
  };

  const handleEdit = (user) => {
    setSelectedUser(user); 
    setDialogRowId(user.id);
    setDialogOpen(true);
  };

  const handleSaveUser = async (payload) => {
  try {
    const method = dialogRowId ? "PATCH" : "POST";
    const url = dialogRowId
      ? `/api/user/update/${dialogRowId}`
      : `/api/user/create`;

    const res = await fetchWithInterceptor(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        "x-access-token": authContext.getToken(),
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) throw new Error("Failed saving user");

    appContext.showMessage(
      dialogRowId ? "User updated successfully" : "User created successfully",
      "success"
    );

    setDialogOpen(false);
    loadUsers(true);

  } catch (err) {
    console.error(err);
    appContext.showMessage("Error saving user", "error");
  }
};

  const handleSavePassword = async (newPassword) => {
    try {
      const res = await fetchWithInterceptor(`/api/user/update/${selectedUser.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": authContext.getToken(),
        },
        body: JSON.stringify({ password: newPassword }),
      });

      if (!res.ok) throw new Error("Failed updating password");

      appContext.showMessage("Password updated successfully", "success");
      setPasswordDialogOpen(false);

    } catch (err) {
      console.error(err);
      appContext.showMessage("Error updating password", "error");
    }
};

  const loadUsers = async (reset = false) => {
    if (loading) {
      return;
    }

    try {
      setLoading(true);

      const params = new URLSearchParams();

      if (filters.fullName) params.append("fullName", filters.fullName);
      if (filters.email) params.append("email", filters.email);
      if (filters.role) params.append("role", filters.role);

      params.append("skip", reset ? 0 : paginationModel.page * PAGE_SIZE);
      params.append("take", PAGE_SIZE);

      if (sortModel.length > 0) {
        params.append("sortField", sortModel[0].field);
        params.append("sortDirection", sortModel[0].sort);
      }
    
      const url = `/api/user/list?${params.toString()}`;

      const response = await fetchWithInterceptor(url, {
        headers: {
          "Content-Type": "application/json",
          "x-access-token": authContext.getToken(),
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch users");
      }

      const data = await response.json();
      
      if (!active.current) return;

      setUsers(data.users || []);
      setRowCount(data.total || 0);

      if (reset) setPaginationModel({ ...paginationModel, page: 0 });
    } catch (err) {
      console.error(err);
      appContext.showMessage("Error loading users", "error");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      field: "fullName",
      headerName: "FULL NAME",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "email",
      headerName: "EMAIL",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "role",
      headerName: "ROLE",
      width: 200,
      renderCell: (params) => <Chip label={params.row.role?.toUpperCase()} size="small" />,
    },
    {
      field: "isActive",
      headerName: "STATUS",
      width: 200,
      renderCell: (params) => (
        <Chip
          label={params.row.isActive ? "Active" : "Inactive"}
          color={params.row.isActive ? "success" : "default"}
          size="small"
        />
      ),
    },
    {
      field: "actions",
      headerName: "ACTIONS",
      width: 150,
      renderCell: (params) => (
        <>
          <Typography
            variant="body2"
            sx={{
              color: "primary.main",
              cursor: "pointer",
              display: "inline-block",
              mr: 1,
            }}
            onClick={() => handleEdit(params.row)}
          >
            EDIT
          </Typography>
          <IconButton size="small" onClick={(e) => handleMenuOpen(e, params.row)}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </>
      ),
    },
  ];

  useEffect(() => {
    loadUsers(false);
    // eslint-disable-next-line
  }, [paginationModel.page]);

  useEffect(() => {
    loadUsers(true);
    // eslint-disable-next-line
  }, [filters]);

  useEffect(() => {
    loadUsers(true);
    // eslint-disable-next-line
  }, [sortModel]);

  return (
    <>
      <Typography variant="body1" sx={{ ml: 3, mt: 3, mr: 5, mb: 1, color: "text.secondary" }}>
        Create, edit, and deactivate ZYN staff users within the ZYN web app. Modify their roles and access permissions.
      </Typography>

      <Container sx={{ pb: 3 }} component="main" maxWidth="100%">
        <Paper sx={{ width: '100%', backgroundColor: 'background.paper', mt: 2, mb: 2 }}>
          <div style={{ padding: 16 }}>
            <Typography variant="h6">Search Filters</Typography>
          </div>

          <Grid 
            container 
            spacing={2} 
            alignItems="center" 
            justifyContent="space-between" 
            sx={{ px: 2, pt: 3, pb: 2 }}
          >

            <Grid 
              size={{ xs: 12, md: 9 }}
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "nowrap",  
              }}
            >
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  label="Full name"
                  name="fullName"
                  variant="outlined"
                  fullWidth
                  value={filters.fullName}
                  onChange={handleFilterChange}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  label="Email"
                  name="email"
                  variant="outlined"
                  fullWidth
                  value={filters.email}
                  onChange={handleFilterChange}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  select
                  label="Role"
                  name="role"
                  variant="outlined"
                  fullWidth
                  value={filters.role}
                  onChange={handleFilterChange}
                >
                  <MenuItem value="">ALL ROLES</MenuItem>
                  <MenuItem value="admin">ADMINISTRATOR</MenuItem>
                  <MenuItem value="supervisor">SUPERVISOR</MenuItem>
                  <MenuItem value="warehouse">WAREHOUSE</MenuItem>
                  <MenuItem value="retail">RETAIL & CONTENT</MenuItem>
                  <MenuItem value="events">EVENTS MANAGER</MenuItem>
                </TextField>
              </Grid>
            </Grid>

            <Grid
              size={{ xs: 12, md: 3 }}
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
              <Button onClick={handleReset}>
                RESET FILTER
              </Button>
            </Grid>
          </Grid>

          <Divider />

          <Grid container sx={{ px: 4, pb: 2, pt: 2, justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              color="primary"
                onClick={() => {
                setDialogRowId(null);
                setDialogOpen(true);
              }}
            >
              CREATE USER
            </Button>
          </Grid>

          <DataGrid
            rows={users}
            columns={columns}
            paginationMode="server"
            sortingMode="server"
            sortModel={sortModel}
            onSortModelChange={(newModel) => { setSortModel(newModel.length ? newModel : defaultOrder) }}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            rowCount={rowCount}
            pageSizeOptions={[PAGE_SIZE]}
            disableSelectionOnClick
            disableColumnFilter
            loading={loading}
            sx={{
              height: 52 * PAGE_SIZE + 120,
              border: "none",
              '& .MuiDataGrid-menuIcon': { display: 'none' },
              '& .MuiDataGrid-columnHeaderTitle': {
                textTransform: 'uppercase',
                fontWeight: 600,
                fontSize: '0.75rem',
                color: 'rgba(58, 53, 65, 0.87)',
              },
              '& .MuiDataGrid-columnsContainer': {
                backgroundColor: 'rgb(249, 250, 252)',
              },
            }}
          />
        </Paper>

        <Menu open={!!anchorEl} anchorEl={anchorEl} onClose={handleMenuClose}>
          <MenuItem onClick={handleToggleStatusClick}>
            {selectedUser?.isActive ? "Deactivate" : "Activate"}
          </MenuItem>
          <MenuItem onClick={handleChangePassword}>Change Password</MenuItem>
        </Menu>
        <UserDialog
          open={dialogOpen}
          rowId={dialogRowId}
          user={selectedUser}
          onClose={() => {
            setDialogOpen(false);
            setDialogRowId(null);
            setSelectedUser(null);
          }}
          onSave={handleSaveUser}
        />
        <ConfirmDialog
          open={confirmOpen}
          title={selectedUser?.isActive ? "Deactivate User" : "Activate User"}
          message={
            selectedUser?.isActive ? (
              <>Are you sure you want to deactivate 
                <strong style={{ fontWeight: 500 }}> "{selectedUser?.fullName}"</strong>?
              </>
            ) : (
              <>Are you sure you want to activate 
                <strong style={{ fontWeight: 500 }}> "{selectedUser?.fullName}"</strong>?
              </>
            )
          }
          onCancel={() => setConfirmOpen(false)}
          onConfirm={() => {
            setConfirmOpen(false);
            pendingAction && pendingAction();
          }}
        />
        <ChangePasswordDialog
          open={passwordDialogOpen}
          user={selectedUser}
          onClose={() => {
            setPasswordDialogOpen(false);
            setSelectedUser(null);
          }}
          onSave={handleSavePassword}
        />
      </Container>
     </>
  );
}